import { Navigate, Route, Routes } from 'react-router';
import { BrandProvider } from '~/config/branding';
import { useSiteMode } from '~/config/siteMode';
import { RoleSelectPage } from '~/features/user/RoleSelectPage';
import { RegistrationRoute } from './RegistrationRoute';

import { ProviderRegistrationRoute } from '~/features/provider/registration-v2/ProviderRegistrationRoute';

import { MarketHeader } from '~/features/market/MarketHeader';
import { VaultHeader } from '~/features/vault/VaultHeader';

import { PlaceholderView } from '~/components/PlaceholderView';
import { LogoutPage } from '~/features/auth/LogoutPage';
import { ProtectedHLXRoute } from './ProtectedHLXRoute';
import { ProtectedVaultRoute } from './ProtectedVaultRoute';

import { DocumentsView } from '~/features/vault/documents';

import { HlxDashboard } from '~/features/market/dashboard/HlxDashboard';

// MCV Feature Components
import { AcademicDetails } from '~/features/vault/academics';
import { AOIDetails } from '~/features/vault/aoi';
import { AttestationDetail } from '~/features/vault/attestations';
import { BoardCertDetails } from '~/features/vault/board-certification';
import { CertificationDetails } from '~/features/vault/certification';
import { CMETrainingDetails } from '~/features/vault/cme-training';
import { EmploymentDetails } from '~/features/vault/employment';
import { LicensesFormWrapper } from '~/features/vault/licenses';
import { MalpracticeView } from '~/features/vault/malpractice';
import { MedicalLicenseDetails } from '~/features/vault/medical-license';
import { PeerRefReqDetails } from '~/features/vault/peer-reference-request';
import { PrivilegesView } from '~/features/vault/privileges';
import { ProfileForm } from '~/features/vault/profile/ProfileForm';
import { HlxAccountSettings } from '~/pages/settings/HlxAccountSettings';
import { Settings } from '~/pages/settings/Settings';

import { FeedbackButton } from '~/components/feedback/FeedbackButton';
import { ChatbotButtonWrapper } from '~/features/chatbot';
import { AdminCreditsPage } from '~/features/employer/billing/AdminCreditsPage';
import { BillingPage } from '~/features/employer/billing/BillingPage';
import { EmployerUserInvitationAccept } from '~/features/employer/colleague-invites/EmployerUserInvitationAccept';
import { TeamPage } from '~/features/employer/colleague-invites/TeamPage';
import { ProviderAssociationList } from '~/features/employer/provider-associations/ProviderAssociationList';
import { ProviderSearchPage } from '~/features/employer/provider-search/ProviderSearchPage';
import { ProviderView } from '~/features/employer/provider-view/ProviderView';
import { EmployerRegistrationRoute } from '~/features/employer/registration-v2/EmployerRegistrationRoutes';
import { B2BOpportunitiesWorkspace } from '~/features/market/b2b-opportunities/B2BOpportunitiesWorkspace';
import { MarketContractList } from '~/features/market/contracts/MarketContractList';
import { JobOpportunitiesRoute } from '~/features/market/job-opportunities/JobOpportunitiesRoute';
import { ListingSearch } from '~/features/market/listing-search/ListingSearch';
import { MsaasListingDetails } from '~/features/market/listings/msaas-listing/MsaasListingDetails';
import { EmployerInfoPage } from '~/features/market/organization';
import { ServiceOpportunities } from '~/features/market/service-opportunities/ServiceOpportunities';
import { ProviderServiceListings } from '~/features/provider-hlx/service-listings';
import { Dashboard } from '~/features/vault/dashboard/Dashboard';
import { SSOCallbackPage } from '~/pages/authentication/SSOCallbackPage';
import { EmployerUserInvitationVerify } from '~/pages/EmployerUserInvitationVerify';
import { Eula } from '~/pages/Eula';
import { Privacy } from '~/pages/Privacy';
import { ProviderAssociationVerify } from '~/pages/ProviderAssociationVerify';
import { InAppFaq } from '~/pages/resources/InAppFaq';
import { Terms } from '~/pages/Terms';
import { AuthCompletePage } from './AuthCompletePage';
import { AuthenticatedSignInRoute } from './AuthenticatedSignInRoute';
import { AuthenticatedSignUpRoute } from './AuthenticatedSignUpRoute';
import { DefaultRoleRedirect } from './DefaultRoleRedirect';
import { EmployerFacilitiesRoute } from './EmployerFacilitiesRoute';
import { HlxCalendarRoute } from './HlxCalendarRoute';
import { HlxLayout } from './HlxLayout';
import { HlxListingsRoute } from './HlxListingsRoute';
import { RoleHomeRedirect } from './RoleHomeRedirect';
import { paths } from './routes';

/**
 * Authenticated Routes Component
 *
 * Contains all routes that require authentication.
 * Routes are organized by prefix:
 * - /vault/*: MedCredVault routes
 * - /hlx/*: HealthcareLX routes
 * - /register/*: Registration routes
 *
 * HLX and vault routes are mounted only for their matching site mode.
 *
 * DefaultRoleRedirect and RegistrationRoute derive post-auth destinations
 * from user state rather than state machines sending ON_NAVIGATE events.
 */
export const AuthenticatedRoutes = () => {
  const siteMode = useSiteMode();
  const isHealthcareLx = siteMode === 'healthcarelx';
  const isMedCredVault = siteMode === 'medcredvault';

  return (
    <>
      <ChatbotButtonWrapper />
      <FeedbackButton />

      <Routes>
        {/* Registration Routes */}
        <Route
          path={paths.register.roleSelect}
          element={
            <RegistrationRoute>
              <RoleSelectPage />
            </RegistrationRoute>
          }
        />
        <Route
          path={paths.register.provider}
          element={
            <RegistrationRoute>
              <ProviderRegistrationRoute />
            </RegistrationRoute>
          }
        />
        <Route
          path={`${paths.legacy.providerRegistrationBase}/*`}
          element={<Navigate to={paths.register.provider} replace />}
        />

        <Route
          path={paths.register.employer}
          element={
            <RegistrationRoute>
              <EmployerRegistrationRoute />
            </RegistrationRoute>
          }
        />

        {/* Shared cross-brand routes */}
        <Route path={paths.logout} element={<LogoutPage />} />
        <Route path={paths.appHome} element={<RoleHomeRedirect />} />
        <Route path={paths.publicAuth.authComplete} element={<AuthCompletePage />} />
        <Route path={`${paths.publicAuth.signIn}/*`} element={<AuthenticatedSignInRoute />} />
        <Route path={`${paths.publicAuth.signInLegacy}/*`} element={<AuthenticatedSignInRoute />} />
        <Route path={`${paths.publicAuth.signUp}/*`} element={<AuthenticatedSignUpRoute />} />
        <Route
          path={`${paths.publicAuth.signUpProvider}/*`}
          element={<AuthenticatedSignUpRoute path={paths.publicAuth.signUpProvider} />}
        />
        <Route
          path={`${paths.publicAuth.signUpEmployer}/*`}
          element={<AuthenticatedSignUpRoute path={paths.publicAuth.signUpEmployer} />}
        />

        {/* HLX Routes - /hlx/* */}

        <Route path={paths.providerAssociation.verify} element={<ProviderAssociationVerify />} />
        <Route
          path={paths.employerUserInvitation.verify}
          element={<EmployerUserInvitationVerify />}
        />
        <Route
          path={paths.employerUserInvitation.accept}
          element={<EmployerUserInvitationAccept />}
        />
        {isHealthcareLx && (
          <Route
            element={
              <BrandProvider brand="hlx">
                <MarketHeader />
              </BrandProvider>
            }
          >
            <Route
              element={
                <ProtectedHLXRoute>
                  <HlxLayout />
                </ProtectedHLXRoute>
              }
            >
              <Route path={paths.hlx.dashboard} element={<HlxDashboard />} />
              <Route path={paths.hlx.settings} element={<HlxAccountSettings />} />
              <Route path={paths.hlx.calendar} element={<HlxCalendarRoute />} />
              <Route path={paths.hlx.msaasListings} element={<MsaasListingDetails />} />
              <Route path={paths.hlx.listings} element={<HlxListingsRoute />} />
              <Route path={paths.hlx.listingSearch} element={<ListingSearch />} />
              <Route path={paths.hlx.contracts} element={<MarketContractList />} />
              <Route path={paths.hlx.billing} element={<BillingPage />} />
              <Route path={paths.hlx.billingAdmin} element={<AdminCreditsPage />} />
              <Route path={paths.hlx.jobOpportunities} element={<JobOpportunitiesRoute />} />
              <Route path={paths.hlx.serviceOpportunities} element={<ServiceOpportunities />} />
              <Route path={paths.hlx.b2bOpportunities} element={<B2BOpportunitiesWorkspace />} />
              <Route path={paths.hlx.myServices} element={<ProviderServiceListings />} />
              <Route path={paths.hlx.providerAssociations} element={<ProviderAssociationList />} />
              <Route path={paths.hlx.team} element={<TeamPage />} />
              <Route path={paths.hlx.providerView} element={<ProviderView />} />
              <Route path={paths.hlx.search} element={<ProviderSearchPage />} />
              <Route path={paths.hlx.employerInfo} element={<EmployerInfoPage />} />
              <Route path={paths.hlx.facilities} element={<EmployerFacilitiesRoute />} />
              {/* [MCV-1724] The header ? opens the knowledge base in the app. */}
              <Route path={paths.hlx.faq} element={<InAppFaq />} />
              <Route
                path={paths.hlx.impersonation}
                element={<PlaceholderView title="Provider Impersonation" />}
              />
              {/* [MCV-1521] Retired route, redirected directly. The
                  registered-route guard in postAuthRedirect would also bounce
                  it to the dashboard via the wildcard; the explicit route just
                  keeps the hop count at one. The literal is deliberate: the
                  path constant is gone and the removal-lock spec keeps it out
                  of paths.hlx. */}
              <Route path="/hlx/profile" element={<Navigate to={paths.hlx.dashboard} replace />} />
            </Route>
          </Route>
        )}

        {/* Vault Routes - /vault/* */}
        {isMedCredVault && (
          <Route
            element={
              <BrandProvider brand="mcv">
                <VaultHeader />
              </BrandProvider>
            }
          >
            <Route
              path={paths.vault.dashboard}
              element={
                <ProtectedVaultRoute>
                  <Dashboard />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.profile}
              element={
                <ProtectedVaultRoute>
                  <ProfileForm />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.aoi}
              element={
                <ProtectedVaultRoute>
                  <AOIDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.settings}
              element={
                <ProtectedVaultRoute>
                  <Settings />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.licenses}
              element={
                <ProtectedVaultRoute>
                  <LicensesFormWrapper />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.boardCertifications}
              element={
                <ProtectedVaultRoute>
                  <BoardCertDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.certifications}
              element={
                <ProtectedVaultRoute>
                  <CertificationDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.academics}
              element={
                <ProtectedVaultRoute>
                  <AcademicDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.documents}
              element={
                <ProtectedVaultRoute>
                  <DocumentsView />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.employment}
              element={
                <ProtectedVaultRoute>
                  <EmploymentDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.privileges}
              element={
                <ProtectedVaultRoute>
                  <PrivilegesView />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.attestations}
              element={
                <ProtectedVaultRoute>
                  <AttestationDetail />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.peerReferences}
              element={
                <ProtectedVaultRoute>
                  <PeerRefReqDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.cmeTraining}
              element={
                <ProtectedVaultRoute>
                  <CMETrainingDetails />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.malpractice}
              element={
                <ProtectedVaultRoute>
                  <MalpracticeView />
                </ProtectedVaultRoute>
              }
            />
            <Route
              path={paths.vault.medicalLicense}
              element={
                <ProtectedVaultRoute>
                  <MedicalLicenseDetails />
                </ProtectedVaultRoute>
              }
            />
            {/* [MCV-1724] The header ? opens the knowledge base in the app. */}
            <Route
              path={paths.vault.faq}
              element={
                <ProtectedVaultRoute>
                  <InAppFaq />
                </ProtectedVaultRoute>
              }
            />
          </Route>
        )}

        {/* Legal / public pages — also accessible while signed in so footer links resolve */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/eula" element={<Eula />} />
        <Route path={paths.publicAuth.ssoCallback} element={<SSOCallbackPage />} />

        {/* Default redirect based on role */}
        <Route path="/" element={<DefaultRoleRedirect />} />
        <Route path="*" element={<DefaultRoleRedirect />} />
      </Routes>
    </>
  );
};
